import { createRootRoute, createRoute, createRouter, Outlet, useRouterState, type RouterHistory } from '@tanstack/react-router';
import { Cargando } from '@tarja/ui';
import { lazy, Suspense, type ComponentType } from 'react';
import { useAuth } from './auth/AuthProvider';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/Login';

/** Envuelve un import dinámico con un límite de Suspense: cada página de ruta se descarga bajo demanda (bundle principal más liviano). */
function pagina(cargar: () => Promise<Record<string, unknown>>, exportado: string): () => JSX.Element {
  const Componente = lazy(() => cargar().then((m) => ({ default: m[exportado] as ComponentType<object> })));
  return () => (
    <Suspense fallback={<Cargando />}>
      <Componente />
    </Suspense>
  );
}

const InicioPage = pagina(() => import('./pages/Inicio'), 'InicioPage');
const OnboardingPage = pagina(() => import('./pages/Onboarding'), 'OnboardingPage');
const MaestrosPage = pagina(() => import('./pages/Maestros'), 'MaestrosPage');
const MiGentePage = pagina(() => import('./pages/Maestros'), 'MiGentePage');
const TarifariosPage = pagina(() => import('./pages/Tarifarios'), 'TarifariosPage');
const TarifarioDetallePage = pagina(() => import('./pages/Tarifarios'), 'TarifarioDetallePage');
const JornadasPage = pagina(() => import('./pages/Jornadas'), 'JornadasPage');
const EventosPage = pagina(() => import('./pages/Eventos'), 'EventosPage');
const CuarentenaPage = pagina(() => import('./pages/Cuarentena'), 'CuarentenaPage');
const RepartosPage = pagina(() => import('./pages/Repartos'), 'RepartosPage');
const ReclamosPage = pagina(() => import('./pages/Reclamos'), 'ReclamosPage');
const PeriodosPage = pagina(() => import('./pages/Periodos'), 'PeriodosPage');
const PeriodoDetallePage = pagina(() => import('./pages/Periodos'), 'PeriodoDetallePage');
const NominaPage = pagina(() => import('./pages/Nomina'), 'NominaPage');
const ReportesPage = pagina(() => import('./pages/Reportes'), 'ReportesPage');
const AuditoriaPage = pagina(() => import('./pages/Auditoria'), 'AuditoriaPage');
const IntegracionesPage = pagina(() => import('./pages/Integraciones'), 'IntegracionesPage');
const DispositivosPage = pagina(() => import('./pages/Dispositivos'), 'DispositivosPage');
const EquipoPage = pagina(() => import('./pages/Equipo'), 'EquipoPage');
const MiCuentaPage = pagina(() => import('./pages/MiCuenta'), 'MiCuentaPage');
const RegistrarTarjaPage = pagina(() => import('./pages/RegistrarTarja'), 'RegistrarTarjaPage');
const RegistroPage = pagina(() => import('./pages/AccesoPublico'), 'RegistroPage');
const InvitacionPage = pagina(() => import('./pages/AccesoPublico'), 'InvitacionPage');
const RestablecerPage = pagina(() => import('./pages/AccesoPublico'), 'RestablecerPage');
const ConfiguracionPage = pagina(() => import('./pages/Configuracion'), 'ConfiguracionPage');

/** Rutas accesibles sin sesión (crear cuenta, aceptar invitación, restablecer contraseña). */
export const RUTAS_PUBLICAS = ['/registro', '/invitacion', '/restablecer'];

export function esRutaPublica(pathname: string, basepath = ''): boolean {
  const p = (basepath && pathname.startsWith(basepath) ? pathname.slice(basepath.length) : pathname).replace(/\/+$/, '') || '/';
  return RUTAS_PUBLICAS.includes(p);
}

/** Sin sesión se muestra el ingreso en cualquier URL (salvo las públicas); tras ingresar se queda en la misma ruta. */
function Raiz() {
  const { estado } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (estado === 'iniciando') return <Cargando texto="Iniciando sesión…" />;
  if (estado !== 'autenticado') return esRutaPublica(pathname, import.meta.env.BASE_URL.replace(/\/$/, '')) ? <Outlet /> : <LoginPage />;
  return <Layout />;
}

function NoEncontrada() {
  return (
    <div className="t-card">
      <h1>Página no encontrada</h1>
      <p>La dirección no existe o fue movida.</p>
    </div>
  );
}

const raiz = createRootRoute({ component: Raiz, notFoundComponent: NoEncontrada });
const r = (path: string, component: () => JSX.Element) => createRoute({ getParentRoute: () => raiz, path, component });

const arbol = raiz.addChildren([
  r('/', InicioPage),
  r('/onboarding', OnboardingPage),
  r('/maestros', MaestrosPage),
  r('/maestros/$objeto', MaestrosPage),
  r('/mi-gente', MiGentePage),
  r('/tarifarios', TarifariosPage),
  r('/tarifarios/$tarifarioId', TarifarioDetallePage),
  r('/jornadas', JornadasPage),
  r('/eventos', EventosPage),
  r('/cuarentena', CuarentenaPage),
  r('/repartos', RepartosPage),
  r('/reclamos', ReclamosPage),
  r('/periodos', PeriodosPage),
  r('/periodos/$periodoId', PeriodoDetallePage),
  r('/nomina', NominaPage),
  r('/reportes', ReportesPage),
  r('/auditoria', AuditoriaPage),
  r('/integraciones', IntegracionesPage),
  r('/dispositivos', DispositivosPage),
  r('/equipo', EquipoPage),
  r('/usuarios', EquipoPage),
  r('/mi-cuenta', MiCuentaPage),
  r('/registrar-tarja', RegistrarTarjaPage),
  r('/registro', RegistroPage),
  r('/invitacion', InvitacionPage),
  r('/restablecer', RestablecerPage),
  r('/configuracion', ConfiguracionPage),
]);

export function crearRouter(history?: RouterHistory) {
  // basepath = base de Vite sin la barra final ('/tarja').
  const basepath = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';
  return createRouter({ routeTree: arbol, basepath, defaultPreload: false, ...(history ? { history } : {}) });
}
